import React, { useEffect, useState, useCallback } from "react";
import api from "../../api";
import { toast } from "../../hooks/use-toast";
import { copyText } from "../../lib/copy";
import { Loader2, FileText, Download, Copy, Building2 } from "lucide-react";

const card = "bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]";
const STATUS = {
  requested: "bg-amber-50 text-amber-700", invoiced: "bg-blue-50 text-blue-700",
  paid: "bg-[#eefafa] text-[#1fb0b0]", declined: "bg-gray-100 text-gray-500", issuing: "bg-purple-50 text-purple-700",
};
const plural = (n) => `${n} voucher${n === 1 ? "" : "s"}`;

async function downloadFile(url, filename) {
  const { data } = await api.get(url, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(data); a.download = filename; a.click();
  URL.revokeObjectURL(a.href);
}

const OrderCard = ({ o, onAction }) => {
  const [notes, setNotes] = useState(o.invoice_notes || "");
  const [open, setOpen] = useState(false);
  const open_ = ["requested", "invoiced", "issuing"].includes(o.status);
  return (
    <div className={card} data-testid={`corp-order-${o.ref}`}>
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <p className="font-semibold text-[#16243b]">{o.company_name} <span className="font-mono text-xs text-[#8a97a8] ml-2">{o.ref}</span></p>
          <p className="text-sm text-[#5a6b80]">{o.contact_name} · {o.contact_email}{o.contact_phone ? ` · ${o.contact_phone}` : ""}{o.po_number ? ` · PO ${o.po_number}` : ""}</p>
          <p className="text-xs text-[#8a97a8] mt-1">Received {o.created_at.slice(0, 10)}{o.scheduled_send_at ? ` · deliver on ${o.scheduled_send_at.slice(0, 10)}` : ""}</p>
        </div>
        <div className="text-right">
          <p className="text-xl font-bold text-[#16243b]">£{o.total.toFixed(2)}</p>
          <p className="text-xs text-[#8a97a8]">{plural(o.count)}</p>
          <span className={`inline-block mt-1 text-xs font-semibold px-3 py-1 rounded-full capitalize ${STATUS[o.status]}`} data-testid={`corp-status-${o.ref}`}>{o.status}</span>
        </div>
      </div>
      <button onClick={() => setOpen(!open)} className="text-xs text-[#1fb0b0] mt-3 font-medium" data-testid={`corp-toggle-${o.ref}`}>{open ? "Hide" : "Show"} recipients</button>
      {open && (
        <ul className="mt-2 text-sm max-h-52 overflow-y-auto divide-y divide-gray-50">
          {o.recipients.map((r, i) => <li key={i} className="py-1.5 flex justify-between"><span>{r.name || "—"} · {r.email}</span><span>£{r.amount.toFixed(2)}</span></li>)}
        </ul>
      )}
      <div className="flex flex-wrap gap-2 mt-4 items-center">
        <button onClick={() => downloadFile(`/business/corporate-orders/${o.id}/invoice.pdf`, `invoice-${o.ref}.pdf`)} data-testid={`corp-invoice-pdf-${o.ref}`}
          className="gp-btn-outline rounded-xl px-3 py-2 text-xs font-medium inline-flex items-center gap-1.5"><FileText size={14} /> Invoice PDF</button>
        {o.status === "paid" && (
          <button onClick={() => downloadFile(`/business/corporate-orders/${o.id}/codes.csv`, `vouchers-${o.ref}.csv`)} data-testid={`corp-codes-${o.ref}`}
            className="gp-btn-outline rounded-xl px-3 py-2 text-xs font-medium inline-flex items-center gap-1.5"><Download size={14} /> Voucher codes</button>
        )}
      </div>
      {open_ && (
        <div className="mt-4 border-t border-gray-100 pt-4 space-y-3">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Payment details for the invoice, e.g. bank, sort code, account number, due date"
            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm" data-testid={`corp-notes-${o.ref}`} />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => onAction(o, "invoice", notes)} data-testid={`corp-send-invoice-${o.ref}`} className="gp-btn-outline rounded-xl px-4 py-2 text-sm font-medium">{o.status === "invoiced" ? "Re-send invoice" : "Email invoice"}</button>
            <button onClick={() => onAction(o, "paid")} data-testid={`corp-mark-paid-${o.ref}`} className="gp-btn-teal rounded-xl px-4 py-2 text-sm font-medium">Mark paid & send vouchers</button>
            <button onClick={() => onAction(o, "decline")} data-testid={`corp-decline-${o.ref}`} className="text-sm text-red-500 px-3">Decline</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function CorporateTab() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/business/corporate-orders").then(({ data }) => setData(data)).catch(() => setData({ orders: [], slug: "" })), []);
  useEffect(() => { load(); }, [load]);

  const act = async (o, action, invoice_notes = "") => {
    const msg = { paid: `Mark ${o.ref} as paid and send ${plural(o.count)} now?`, decline: `Decline order ${o.ref}?` }[action];
    if (msg && !window.confirm(msg)) return;
    try {
      const { data: r } = await api.put(`/business/corporate-orders/${o.id}`, { action, invoice_notes });
      const desc = action === "invoice" ? (r.emailed ? "Invoice emailed to the company." : "Saved. Email isn't set up, so download the PDF and send it yourself.")
        : action === "paid" ? `${r.issued} vouchers issued${r.scheduled ? " and scheduled" : `, ${r.emailed} emailed`}.` : "Order declined.";
      toast({ title: "Done", description: desc }); load();
    } catch (e) { toast({ title: "Error", description: e?.response?.data?.detail || "Failed", variant: "destructive" }); }
  };

  if (!data) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;
  const link = `${window.location.origin}/store/${data.slug}/corporate`;
  return (
    <div className="space-y-6 max-w-[900px]" data-testid="corporate-tab">
      <div className={`${card} flex flex-col sm:flex-row gap-4 sm:items-center`}>
        <Building2 className="text-[#2ec9c9] shrink-0" size={28} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#16243b]">Your corporate ordering page</p>
          <p className="text-sm text-[#5a6b80]">Share it with companies. It's also linked from your storefront.</p>
          <code className="text-xs bg-[#f6f8fb] rounded-lg px-3 py-1.5 mt-2 inline-block truncate max-w-full" data-testid="corp-page-url">{link}</code>
        </div>
        <button onClick={() => copyText(link, "Link copied")} data-testid="corp-copy-link" className="gp-btn-outline rounded-xl px-4 py-2 text-sm font-medium inline-flex items-center gap-2"><Copy size={15} /> Copy link</button>
      </div>
      {data.orders.length === 0 ? <div className={card}><p className="text-sm text-[#8a97a8]" data-testid="corp-empty">No corporate orders yet.</p></div>
        : data.orders.map((o) => <OrderCard key={o.id} o={o} onAction={act} />)}
    </div>
  );
}
